import { i18n } from "../../Common/_translations";

export enum IntegrationCompany {
    AVAIBOOK = 'Avaibook',
    HOSTIFY = 'Hostify',
    OCTORATE = 'Octorate',
    GENERIC = 'Generic',
    KROSS_BOOKING = 'Kross Booking',
    THREE_ANGULAR = 'Three Angular',
    SMOOBU = 'Smoobu',
    AVANTIO = 'Avantio',
    BOOKIPRO = 'Bookipro',
    ICNEA = 'Icnea',
    LODGIFY = 'Lodgify',
    GUESTY = 'Guesty',
    ENSO_CONNECT = 'Enso Connect',
    // Not a PMS: the manager types the reservations in by hand. It exists so a
    // location with no PMS can still have an integration -- and therefore an
    // invitation group with rooms -- to hang manual reservations from.
    MANUAL = 'Manual',
}

// UI-only: tags each PMS card with how it connects. It is NOT the backend's
// integration_type (whose numbering differs): the backend derives that from
// the company and ignores any value sent (BAC-325).
export enum LinkType {
    API_ENDPOINT,
    ICAL,
    LINK,
    WEBHOOK,
    DEVELOPERS,
}

// Health of the last sync attempt for an integration.
export type SyncStatus = 'idle' | 'running' | 'ok' | 'error';

export const SyncStatusValue = {
    IDLE: 'idle',
    RUNNING: 'running',
    OK: 'ok',
    ERROR: 'error',
} as const;

interface IntegrationGroup {
    name?: string;
    type?: number;
    id: number;
}

export interface ServerReservation {
    id: number;
    uid: string;
    status: number;
    reservation_id: string;
    email: string;
    reservation_start: Date;
    reservation_end: Date;
    created_on: Date;
    modified_on: Date;
    integration: number;
}

export interface Reservation extends Omit<ServerReservation, 'created_on' | 'modified_on' | 'reservation_id' | 'reservation_start' | 'reservation_end'>{
    reservationId: string;
    reservationStart: Date;
    reservationEnd: Date;
    createdOn: Date;
    modifiedOn: Date;
}
export interface IntegrationOwner {
    type: 'user' | 'organization';
    name: string;
}

export interface ServerIntegration {
    id: number,
    uid: string,
    company: IntegrationCompany;
    // What the manager calls this PMS account. Only templates carry it; a
    // location integration inherits the name of the template it hangs from.
    name?: string;
    // Which IntegrationTemplate governs this integration: the one whose
    // credentials and settings a save propagates to it. Null for the rows that
    // hang from none (created before the FK existed, or orphaned by a deleted
    // template), which the UI shows as "sin plantilla".
    template_uid?: string | null;
    // The account's readable name, sent alongside the uid because the uid is
    // only resolvable by whoever can list that template: a colleague's personal
    // account is invisible here, and the integration would read as having none.
    template_name?: string | null;
    // Whose account this is, when it is not the caller's own: an Everou
    // superuser also lists the customers' accounts governing the locations
    // they manage (BAC-673). Null on the caller's own rows.
    owner?: IntegrationOwner | null;
    // The backend's own enum, not LinkType: derived from the company (BAC-325).
    integration_type: number;
    invitation_group: IntegrationGroup[];
    link?: string;
    create_public_url?: boolean;
    created_on: Date;
    modified_on: Date;
    // Last time reservations were pulled from the PMS. Only the pull-based
    // integrations update it; webhook/developers ones leave it null.
    last_reservations_checked?: string;
    // Whether the integration supports a manual force-sync (pull-based PMSs).
    // Webhook/developers integrations push reservations and report false.
    can_force_sync?: boolean;
    // Health of the last sync attempt: idle | running | ok | error.
    sync_status?: SyncStatus;
    // Concrete error message of the last failed sync (invalid key, expired token, ...).
    sync_error?: string;
    // Last sync attempt timestamp (ISO), regardless of its outcome.
    last_sync_at?: string;
    // false means the stored credentials are dead -> the user must edit the integration.
    apikey_is_valid?: boolean;
    apikey?: string;
    // Smoobu: whether an HMAC API secret is stored. The secret itself is write-only.
    has_api_secret?: boolean;
    host_id?: string|number;
    handler_url?: string;
    hotel_id?:string;
    api_endpoint_token?: string;
    room_id?:number;
    host_name?:string;
    room_name?:string;
    check_in?:string;
    check_out?:string;
    username?:string;
    password?:string;
    send_invitation_url?:boolean;
    send_invitation_url_from_pms?:boolean;
    days_for_invitation_create?:number;
}

export interface ServerIntegrationSend extends Omit<ServerIntegration, 'invitation_group' | 'integration_type' | 'uid' | 'created_on' | 'id' | 'modified_on' | 'has_api_secret'> {
    invitation_group?: number[];
    uid?: string;
    // Absent keeps the stored secret, blank clears it.
    api_secret?: string | null;
}

export interface Integration extends Omit<ServerIntegration, 'uid' | 'invitation_group' | 'integration_type' | 'create_public_url' | 'id' | 'modified_on' | 'created_on' | 'last_reservations_checked' | 'can_force_sync' | 'sync_status' | 'sync_error' | 'last_sync_at' | 'apikey_is_valid' | 'has_api_secret' | 'template_uid' | 'template_name'> {
    uid: string;
    templateUid?: string | null;
    templateName?: string | null;
    groups?: IntegrationGroup[];
    createPublicUrl?: boolean;
    reservations?: Reservation[];
    createdOn?: Date;
    modifiedOn?: Date;
    lastReservationsChecked?: string;
    canForceSync?: boolean;
    syncStatus?: SyncStatus;
    syncError?: string;
    lastSyncAt?: string;
    apikeyIsValid?: boolean;
    hasApiSecret?: boolean;
    // Only set when the host typed a new secret: undefined keeps the stored one.
    apiSecret?: string | null;
    sendInvitationURL?: boolean;
    sendInvitationURLFromPMS?: boolean;
    daysForInvitationCreate?: number;
    // Set client-side when a template comes from the user's personal (legacy)
    // scope instead of the organization's, so a picker offering both can tell
    // apart two templates of the same company.
    isPersonal?: boolean;
}

export type LocalEditableIntegration = Integration & { editable: boolean };

export interface KrossBookingIntegration {
    uid: string;
    properties?: KrossBookingProperty[]
}

export interface KrossBookingProperty {
    id_property: number,
    name: string,
    city?: string,
    rooms?: KrossBookingRoom[]
}

export interface HostifyListing {
    id: number,
    nickname: string
}

export interface SmoobuApartment {
    id: number,
    name: string
}

export interface KrossBookingRoom {
    id_room: number,
    number: string
}

export interface OctorateAccommodation {
    id: number,
    name: string
}

export interface AvaibookAccommodation {
    id: number,
    name: string
}

export interface AvantioAccommodation {
    id: string,
    name: string
}

export interface LodgifyProperty {
    id: number,
    name: string
}

export interface GuestyListing {
    _id: string,
    title: string
}

export const EmptyGenericIntegration: LocalEditableIntegration = { createPublicUrl: true, link: '', uid: '', company: IntegrationCompany.GENERIC, groups: [], editable: true } 
export const EmptyAvaibookIntegration: LocalEditableIntegration = { createPublicUrl: true, link: '', uid: '', company: IntegrationCompany.AVAIBOOK, groups: [], editable: true } 
export const EmptyHostifyIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.HOSTIFY, groups: [], editable: true } 
export const EmptyKrossBookingIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.KROSS_BOOKING, groups: [], editable: true } 
export const EmptyThreeAngularIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.THREE_ANGULAR, groups: [], editable: true } 
export const EmptySmoobuIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.SMOOBU, groups: [], editable: true }
export const EmptyAvantioIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.AVANTIO, groups: [], editable: true }
export const EmptyBookiproIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.BOOKIPRO, groups: [], editable: true } 
export const EmptyIcneaIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.ICNEA, groups: [], editable: true } 
export const EmptyLodgifyIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.LODGIFY, groups: [], editable: true } 
export const EmptyGuestyIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.GUESTY, groups: [], editable: true }
export const EmptyEnsoConnectIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.ENSO_CONNECT, groups: [], editable: true }
export const EmptyOctorateIntegration: LocalEditableIntegration = { createPublicUrl: true, uid: '', company: IntegrationCompany.OCTORATE, groups: [], editable: true }
export const EmptyManualIntegration: LocalEditableIntegration = { createPublicUrl: true, sendInvitationURL: false, uid: '', company: IntegrationCompany.MANUAL, groups: [], editable: true }


// The credentials an account of `company` is made of, as an integration's
// row carries them. The legacy app.baintex.com flow saved them on the row
// itself, so an account created for such an orphan starts from these
// instead of a blank form (BAC-672). Only what is set travels: the empty
// row's defaults (createPublicUrl) are kept for the rest.
export type IntegrationAccountDraft = Pick<Integration,
    'apikey' | 'username' | 'password' | 'hotel_id' | 'link' | 'check_in' | 'check_out'
    | 'sendInvitationURL' | 'sendInvitationURLFromPMS' | 'daysForInvitationCreate'>;

const ACCOUNT_DRAFT_FIELDS: (keyof IntegrationAccountDraft)[] = [
    'apikey', 'username', 'password', 'hotel_id', 'link', 'check_in', 'check_out',
    'sendInvitationURL', 'sendInvitationURLFromPMS', 'daysForInvitationCreate',
];

export const accountDraftOf = (integration: Partial<Integration>): IntegrationAccountDraft => {
    const draft: IntegrationAccountDraft = {};
    for (const field of ACCOUNT_DRAFT_FIELDS) {
        const value = integration[field];
        if (value !== undefined && value !== null && value !== '') {
            (draft as any)[field] = value;
        }
    }
    return draft;
};

export const emptyIntegrationOf = (integrationCompany: IntegrationCompany): LocalEditableIntegration => {
    switch (integrationCompany) {
        case IntegrationCompany.AVAIBOOK: return EmptyAvaibookIntegration;
        case IntegrationCompany.HOSTIFY: return EmptyHostifyIntegration;
        case IntegrationCompany.KROSS_BOOKING: return EmptyKrossBookingIntegration;
        case IntegrationCompany.SMOOBU: return EmptySmoobuIntegration;
        case IntegrationCompany.AVANTIO: return EmptyAvantioIntegration;
        case IntegrationCompany.LODGIFY: return EmptyLodgifyIntegration;
        case IntegrationCompany.GUESTY: return EmptyGuestyIntegration;
        case IntegrationCompany.ENSO_CONNECT: return EmptyEnsoConnectIntegration;
        case IntegrationCompany.MANUAL: return EmptyManualIntegration;
        case IntegrationCompany.OCTORATE: return EmptyOctorateIntegration;
        case IntegrationCompany.ICNEA: return EmptyIcneaIntegration;
        case IntegrationCompany.BOOKIPRO: return EmptyBookiproIntegration;
        case IntegrationCompany.THREE_ANGULAR: return EmptyThreeAngularIntegration;
        default: return EmptyGenericIntegration;
    }
};

// The prefilled row of each draft, built once: the panels rebuild their rows
// on every list refresh, and the inputs reset their fields whenever their row
// is a new object -- a refresh after saving or deleting another account used
// to wipe whatever had been typed on the draft row. Same idea as the blank
// rows, which are the constant Empty* objects.
const draftRows = new WeakMap<IntegrationAccountDraft, Map<IntegrationCompany, LocalEditableIntegration>>();

const draftRowOf = (
    integrationCompany: IntegrationCompany,
    draft: IntegrationAccountDraft,
): LocalEditableIntegration => {
    let rows = draftRows.get(draft);
    if (!rows) {
        rows = new Map();
        draftRows.set(draft, rows);
    }
    let row = rows.get(integrationCompany);
    if (!row) {
        row = { ...emptyIntegrationOf(integrationCompany), ...draft, editable: true };
        rows.set(integrationCompany, row);
    }
    return row;
};

// The rows a company's panel shows: the saved accounts, read-only, plus a
// blank editable one when there is none -- or, when the panel was opened to
// create an account for an orphan integration, an editable one prefilled
// with the orphan's credentials (`draft`), after the saved accounts.
export const integrationsByCompany = (
    integrations: Integration[] = [],
    integrationCompany: IntegrationCompany,
    draft: IntegrationAccountDraft | null = null,
): LocalEditableIntegration[] => {

    let res = integrations.filter(({ company }) => company === integrationCompany).map(integration => ({...integration, editable: false}));

    if (draft) {
        return [...res, draftRowOf(integrationCompany, draft)];
    }
    if (!res?.length) {
        res = [emptyIntegrationOf(integrationCompany)];
    }
    
    return res;

}

export const IntegrationReservationStatus = {
    STATUS_PENDING: 0,
    STATUS_CONFIRMED: 1,
    STATUS_ACTIVE: 2,
    STATUS_CANCELLED: 3 
}

export const IntegrationReservationStatusLabel = {
    [IntegrationReservationStatus.STATUS_PENDING]: i18n.t('mod_integrations_reservation_pending'),
    [IntegrationReservationStatus.STATUS_CONFIRMED]: i18n.t('mod_integrations_reservation_confirmed'),
    [IntegrationReservationStatus.STATUS_ACTIVE]: i18n.t('mod_integrations_reservation_active'),
    [IntegrationReservationStatus.STATUS_CANCELLED]: i18n.t('mod_integrations_reservation_cancelled')
}